import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { trackPageView, startHeartbeat, checkAndTrackPWAInstall } from "./services/analytics";

// Public Pages
import LandingPage from "./pages/public/LandingPage";
import PublicStorePage from "./pages/public/PublicStorePage";
import PublicHomePage from "./pages/public/PublicHomePage";
import CouponRedirect from "./pages/public/CouponRedirect";

// Auth Pages
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";

// Legal Pages
import TermosDeUso from "./pages/legal/TermosDeUso";
import PoliticaPrivacidade from "./pages/legal/PoliticaPrivacidade";
import FAQ from "./pages/legal/FAQ";
import SobreNos from "./pages/legal/SobreNos";

// Express Pages (Leva e Traz / Ponto a Ponto)
import ExpressServicesPage from "./pages/express/ExpressServicesPage";
import ExpressRequestPage from "./pages/express/ExpressRequestPage";
import ExpressPaymentPage from "./pages/express/ExpressPaymentPage";
import ExpressTrackingPage from "./pages/express/ExpressTrackingPage";
import ExpressDriverPage from "./pages/express/ExpressDriverPage";

// Cliente Pages
import ClienteHome from "./pages/cliente/ClienteHome";
import ProductDetail from "./pages/cliente/ProductDetail";
import StoreDetail from "./pages/cliente/StoreDetail";
import Cart from "./pages/cliente/Cart";
import Checkout from "./pages/cliente/Checkout";
import Orders from "./pages/cliente/Orders";
import OrderTracking from "./pages/cliente/OrderTracking";
import PixPayment from "./pages/cliente/PixPayment";
import CardPaymentPage from "./pages/cliente/CardPaymentPage";
import ReviewOrder from "./pages/cliente/ReviewOrder";

// Lojista Pages
import LojistaDashboard from "./pages/lojista/LojistaDashboard";
import CreateStore from "./pages/lojista/CreateStore";
import EditStore from "./pages/lojista/EditStore";
import ProductsList from "./pages/lojista/ProductsList";
import ProductForm from "./pages/lojista/ProductForm";
import ProductPreview from "./pages/lojista/ProductPreview";

// Entregador Pages
import EntregadorDashboard from "./pages/entregador/EntregadorDashboard";
import DeliveryDetail from "./pages/entregador/DeliveryDetail";
import DriverDocuments from "./pages/entregador/DriverDocuments";
import DriverEarnings from "./pages/entregador/DriverEarnings";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import SystemReport from "./pages/admin/SystemReport";

// Chat Pages
import ConversationsList from "./pages/chat/ConversationsList";

// Common Components
import InstallPWA from "./components/common/InstallPWA";

// Protected Route Component
const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedTypes && !allowedTypes.includes(user?.user_type)) {
    switch (user?.user_type) {
      case 'cliente':
        return <Navigate to="/cliente" replace />;
      case 'lojista':
        return <Navigate to="/lojista" replace />;
      case 'entregador':
        return <Navigate to="/entregador" replace />;
      case 'admin':
        return <Navigate to="/admin" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  return children;
};

// Home redirect based on user type
const HomeRedirect = () => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  switch (user?.user_type) {
    case 'cliente':
      return <Navigate to="/cliente" replace />;
    case 'lojista':
      return <Navigate to="/lojista" replace />;
    case 'entregador':
      return <Navigate to="/entregador" replace />;
    case 'admin':
      return <Navigate to="/admin" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

// Analytics Tracker Component
const AnalyticsTracker = ({ children }) => {
  const location = useLocation();
  
  useEffect(() => {
    // Registrar visita de página
    trackPageView(location.pathname);
  }, [location.pathname]);
  
  useEffect(() => {
    // Iniciar heartbeat e verificar instalação PWA
    startHeartbeat();
    checkAndTrackPWAInstall();
  }, []);
  
  return children;
};

function AppRoutes() {
  return (
    <AnalyticsTracker>
    <Routes>
      {/* Landing Page - Public */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/esqueci-senha" element={<ForgotPassword />} />
      <Route path="/redefinir-senha" element={<ResetPassword />} />
      <Route path="/termos" element={<TermosDeUso />} />
      <Route path="/privacidade" element={<PoliticaPrivacidade />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/sobre" element={<SobreNos />} />
      
      {/* Cupom de desconto - Rota pública */}
      <Route path="/cupom/:code" element={<CouponRedirect />} />
      
      {/* Public Store Routes - Vitrine sem login */}
      <Route path="/loja/:slug" element={<PublicStorePage />} />
      <Route path="/vitrine" element={<PublicHomePage />} />
      <Route path="/produto/:id" element={<ProductDetail />} />

      {/* Express Services (Leva e Traz / Ponto a Ponto) - Public info page */}
      <Route path="/express" element={<ExpressServicesPage />} />
      <Route path="/express/solicitar" element={<ProtectedRoute><ExpressRequestPage /></ProtectedRoute>} />
      <Route path="/express/pagamento/:orderId" element={<ProtectedRoute><ExpressPaymentPage /></ProtectedRoute>} />
      <Route path="/express/acompanhar/:orderId" element={<ProtectedRoute><ExpressTrackingPage /></ProtectedRoute>} />
      <Route path="/entregador/express" element={<ProtectedRoute allowedTypes={['entregador']}><ExpressDriverPage /></ProtectedRoute>} />

      {/* Home Redirect (for logged users) */}
      <Route path="/home" element={<HomeRedirect />} />

      {/* Cliente Routes - Também acessível por entregadores (podem comprar) */}
      <Route path="/cliente" element={<ProtectedRoute allowedTypes={['cliente', 'entregador']}><ClienteHome /></ProtectedRoute>} />
      <Route path="/cliente/produto/:id" element={<ProtectedRoute allowedTypes={['cliente', 'entregador']}><ProductDetail /></ProtectedRoute>} />
      <Route path="/cliente/loja/:id" element={<ProtectedRoute allowedTypes={['cliente', 'entregador']}><StoreDetail /></ProtectedRoute>} />
      <Route path="/cliente/carrinho" element={<ProtectedRoute allowedTypes={['cliente', 'entregador']}><Cart /></ProtectedRoute>} />
      <Route path="/cliente/checkout/:storeId" element={<ProtectedRoute allowedTypes={['cliente', 'entregador']}><Checkout /></ProtectedRoute>} />
      <Route path="/cliente/pedidos" element={<ProtectedRoute allowedTypes={['cliente', 'entregador']}><Orders /></ProtectedRoute>} />
      <Route path="/cliente/pedido/:id" element={<ProtectedRoute allowedTypes={['cliente', 'entregador']}><OrderTracking /></ProtectedRoute>} />
      <Route path="/cliente/pagamento/pix/:orderId" element={<ProtectedRoute allowedTypes={['cliente', 'entregador']}><PixPayment /></ProtectedRoute>} />
      <Route path="/cliente/pagamento/cartao/:orderId" element={<ProtectedRoute allowedTypes={['cliente', 'entregador']}><CardPaymentPage /></ProtectedRoute>} />
      <Route path="/cliente/avaliar/:orderId" element={<ProtectedRoute allowedTypes={['cliente', 'entregador']}><ReviewOrder /></ProtectedRoute>} />

      {/* Lojista Routes */}
      <Route path="/lojista" element={<ProtectedRoute allowedTypes={['lojista']}><LojistaDashboard /></ProtectedRoute>} />
      <Route path="/lojista/loja/criar" element={<ProtectedRoute allowedTypes={['lojista']}><CreateStore /></ProtectedRoute>} />
      <Route path="/lojista/loja/editar" element={<ProtectedRoute allowedTypes={['lojista']}><EditStore /></ProtectedRoute>} />
      <Route path="/lojista/produtos" element={<ProtectedRoute allowedTypes={['lojista']}><ProductsList /></ProtectedRoute>} />
      <Route path="/lojista/produto/novo" element={<ProtectedRoute allowedTypes={['lojista']}><ProductForm /></ProtectedRoute>} />
      <Route path="/lojista/produto/:id" element={<ProtectedRoute allowedTypes={['lojista']}><ProductForm /></ProtectedRoute>} />
      <Route path="/lojista/produto/:id/preview" element={<ProtectedRoute allowedTypes={['lojista']}><ProductPreview /></ProtectedRoute>} />

      {/* Entregador Routes */}
      <Route path="/entregador" element={<ProtectedRoute allowedTypes={['entregador']}><EntregadorDashboard /></ProtectedRoute>} />
      <Route path="/entregador/entrega/:id" element={<ProtectedRoute allowedTypes={['entregador']}><DeliveryDetail /></ProtectedRoute>} />
      <Route path="/entregador/documentos" element={<ProtectedRoute allowedTypes={['entregador']}><DriverDocuments /></ProtectedRoute>} />
      <Route path="/entregador/ganhos" element={<ProtectedRoute allowedTypes={['entregador']}><DriverEarnings /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute allowedTypes={['admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/relatorio" element={<ProtectedRoute allowedTypes={['admin']}><SystemReport /></ProtectedRoute>} />

      {/* Chat Routes */}
      <Route path="/mensagens" element={<ProtectedRoute><ConversationsList /></ProtectedRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </AnalyticsTracker>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <div className="App">
            <AppRoutes />
            <InstallPWA />
          </div>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
